import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../../../config';
import { useSelector, useDispatch } from "react-redux";
import { setUser } from '../../../features/user/userSlice';
import { Modal } from "react-bootstrap";
import { toast } from "../../common/Toast";
import {
  COUNTRY_CODES,
  findCountryByIso,
  pickCountryIso,
  formatPhoneDisplay,
  parsePhoneInput,
  isPlausibleNationalNumber,
  toDialNumber,
  NATIONAL_NUMBER_MAX_DIGITS,
} from '../../../utils/countryCodes';

const MyContactSetting = ({ userInfo, token, updateUserPhone }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [contactStatus, setContactStatus] = useState("premiumMembers");
  const [isEditing, setIsEditing] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(userInfo?.phone || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [phoneOTP, setPhoneOTP] = useState("");
  const [countdown, setCountdown] = useState(0);
  const dispatch = useDispatch();

  // Country row selected in the dropdown — kept in state (not re-derived from
  // the dial code each render) because many countries share a code (+1 = US,
  // Canada, Jamaica...) and re-deriving would snap "Canada" back to the US.
  const savedIso = () => pickCountryIso(userInfo?.phone_country_code, userInfo?.country);
  const [selectedIso, setSelectedIso] = useState(savedIso);
  const phoneCountryCode = findCountryByIso(selectedIso)?.dial || '';
  const savedCode = findCountryByIso(savedIso())?.dial || '';
  const unchanged = phoneNumber === (userInfo?.phone || '') && phoneCountryCode === savedCode;

  // What the OTP service receives: the number as typed, the dialling code, and
  // the full number ready to text (a trunk "0" like UK 07911... is dropped:
  // +447911...). Sent identically on send and verify so both refer to the same number.
  const phonePayload = () => ({
    phone: phoneNumber,
    phoneCountryCode,
    fullPhone: toDialNumber(phoneCountryCode, phoneNumber),
  });

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const handleEditClick = () => {
    setPhoneNumber(userInfo?.phone || '');
    setSelectedIso(savedIso());
    setError('');
    setIsEditing(true);
  };

  const handleCodeChange = (e) => {
    setSelectedIso(e.target.value);
  };

  // Digits only. No "10 digits exactly" rule any more — numbers differ in
  // length by country. A pasted international number ("+44 7911 123456") has
  // its code read off the front and the dropdown follows it.
  const handlePhoneChange = (e) => {
    const parsed = parsePhoneInput(e.target.value, userInfo?.country);
    setPhoneNumber(parsed.phone.replace(/\D/g, '').slice(0, NATIONAL_NUMBER_MAX_DIGITS));
    if (parsed.dial) setSelectedIso(parsed.iso);
  };

  const sendOTP = async () => {
    if (!phoneCountryCode) {
      setError('Please select your country code');
      return;
    }
    if (!isPlausibleNationalNumber(phoneNumber)) {
      setError('Please enter a valid phone number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.post(`${config.baseURL}/api/auth/send-phone-otp`, phonePayload());
      
      setCountdown(60);
      setShowOTPModal(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTPAndUpdatePhone = async () => {
    try {
      setLoading(true);
      setError('');
      
      // First verify the OTP
      await axios.post(`${config.baseURL}/api/auth/verify-phone-otp`, { 
        ...phonePayload(),
        otp: phoneOTP 
      });
      
      // If OTP is verified, update the phone number
      const res = await axios.put(
        `${config.baseURL}/api/profile/profile-settings`,
        { phone: phoneNumber, phoneCountryCode, contactStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (res.data.success) {
        setShowOTPModal(false);
        toast.success("Phone number updated successfully");
        
        const updatedUser = {
          ...userInfo,
          phone: phoneNumber,
          phone_country_code: phoneCountryCode,
          phone_verified: 1 // Mark as verified
        };
        
        dispatch(setUser({
          userInfo: updatedUser,
          token: token,
        }));
        
        setIsEditing(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP or failed to update phone number. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    if (countdown > 0) return;
    await sendOTP();
  };

  const handleCancelEdit = () => {
    setPhoneNumber(userInfo?.phone || '');
    setSelectedIso(savedIso());
    setIsEditing(false);
    setError('');
    setShowOTPModal(false);
  };

  // Countdown effect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSubmitSettings = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await axios.put(
        `${config.baseURL}/api/profile/profile-settings`,
        { phone: userInfo?.phone, contactStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess('Contact settings updated successfully!');
      setTimeout(() => {
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update contact settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadContactSettings = async () => {
      try {
        const response = await axios.get(
          `${config.baseURL}/api/profile/profile-settings`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        if (response.data.settings?.display_contact_status) {
          setContactStatus(response.data.settings.display_contact_status);
        }
      } catch (error) {
        console.error('Failed to load contact settings:', error);
      }
    };

    if (isOpen) {
      loadContactSettings();
    }
  }, [isOpen]);

  return (
    <div className="accordion-item">
      <h2 className="accordion-header" id="headingTwo">
        <button
          className={`accordion-button ${isOpen ? '' : 'collapsed'}`}
          type="button"
          onClick={toggleAccordion}
        >
          My Contact Settings
        </button>
      </h2>

      {isOpen && (
        <div className="accordion-collapse collapse show">
          <div className="accordion-body">
            <div className="card-setting">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
                  <div className='w-100'>
                    <div className="fw-semibold">Contact Number</div>
                    {isEditing ? (
                      <div className="mt-2 d-flex flex-column gap-2">
                        <div className="d-flex gap-2">
                          <select
                            aria-label="Country code"
                            className="form-select border-0"
                            value={selectedIso}
                            onChange={handleCodeChange}
                            style={{ flex: '0 0 42%', minWidth: 0 }}
                          >
                            {!selectedIso && <option value="">Code</option>}
                            {COUNTRY_CODES.map(c => (
                              <option key={c.iso} value={c.iso}>
                                {c.name} ({c.dial})
                              </option>
                            ))}
                          </select>
                          <input
                            type="tel"
                            inputMode="tel"
                            aria-label="Phone number"
                            className="form-control border-0"
                            value={phoneNumber}
                            onChange={handlePhoneChange}
                            placeholder="Phone number (without country code)"
                            style={{ flex: 1, minWidth: 0 }}
                          />
                        </div>
                        {error && !showOTPModal && (
                          <div className="text-danger small">{error}</div>
                        )}
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-cancel"
                            onClick={handleCancelEdit}
                            disabled={loading}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-submit"
                            onClick={sendOTP}
                            disabled={loading || !phoneCountryCode || !isPlausibleNationalNumber(phoneNumber) || unchanged}
                          >
                            {loading ? 'Sending OTP...' : 'Send OTP'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {formatPhoneDisplay({
                          phone: userInfo?.phone,
                          phoneCountryCode: userInfo?.phone_country_code,
                          country: userInfo?.country,
                        }) || 'Not added'}
                        {userInfo?.phone && (
                          <span className="verified-badge ms-2">
                            {userInfo?.phone_verified === 0 ? (
                              <><i className="fa fa-times-circle text-danger" aria-hidden="true"></i> Not Verified</>
                            ) : (
                              <><i className="fa fa-check-circle" aria-hidden="true"></i> Verified</>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {!isEditing && (
                    <button
                      className="btn btn-link text-primary p-0"
                      onClick={handleEditClick}
                    >
                      Edit
                    </button>
                  )}
                </div>

              

                <div className="shadi-instruction mt-3">
                  <p><i className="fa fa-lock" aria-hidden="true"></i> futurefamilyai.com does not share personal details / contact information with third parties</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      <Modal
        show={showOTPModal}
        onHide={() => setShowOTPModal(false)}
        backdrop="static"
        keyboard={false}
        centered
        size="sm"
        className="letsbeginmodal"
      >
        <Modal.Header>
          <h5 className="modal-title">Verify Phone Number</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowOTPModal(false)}
          ></button>
        </Modal.Header>
        <Modal.Body>
          <p>We've sent a 4-digit OTP to {phoneCountryCode} {phoneNumber}</p>
          <div className="mb-3">
            <input
              type="text"
              className="form-control text-center otp-input"
              value={phoneOTP}
              onChange={(e) => setPhoneOTP(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="----"
              maxLength={4}
            />
          </div>
          {error && <div className="alert alert-danger">{error}</div>}

          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowOTPModal(false);
                setPhoneOTP("");
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn py-2 btn-filled mb-2"
              onClick={verifyOTPAndUpdatePhone}
              disabled={phoneOTP.length !== 4 || loading}
            >
              {loading ? "Verifying..." : "Verify & Update"}
            </button>
            <div className="text-center mb-3">
              <a
                href="#"
                className="text-primary text-decoration-none"
                onClick={(e) => {
                  e.preventDefault();
                  resendOTP();
                }}
                style={{ opacity: countdown > 0 ? 0.5 : 1 }}
              >
                Resend OTP {countdown > 0 ? `(${countdown}s)` : ""}
              </a>
            </div>
          </Modal.Footer>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MyContactSetting;